import { MaterialIcons } from '@expo/vector-icons';
import React, { useEffect, useState } from 'react';
import { ActivityIndicator, Alert, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useCampaignStore } from '@/stores/campaignStore';
import { syncService } from '@/utils/syncService';

import { ThemedText } from './ThemedText';

interface SyncStatusIndicatorProps {
  showDetails?: boolean;
  onSyncComplete?: (result: any) => void;
}

export const SyncStatusIndicator: React.FC<SyncStatusIndicatorProps> = ({ 
  onSyncComplete 
}) => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState({ voters: 0, addresses: 0, telling: 0 });
  const insets = useSafeAreaInsets();

  // Subscribe to store changes instead of polling
  useEffect(() => {
    const calculatePendingCount = (state: any) => {
      const { cachedData } = state;
      
      let totalVoters = 0;
      let totalAddresses = 0;
      let totalTelling = 0;

      // Iterate through all branches and campaigns
      for (const branchId in cachedData) {
        const branchData = cachedData[branchId];
        if (branchData?.campaigns) {
          for (const campaignId in branchData.campaigns) {
            const campaignData = branchData.campaigns[campaignId];
            if (campaignData) {
              totalVoters += Object.keys(campaignData.voterUpdates || {}).length;
              totalAddresses += Object.keys(campaignData.addressUpdates || {}).length;
              totalTelling += Object.keys(campaignData.tellingUpdates || {}).length;
            }
          }
        }
      }

      return {
        voters: totalVoters,
        addresses: totalAddresses,
        telling: totalTelling,
      };
    };

    // Subscribe to changes in the relevant parts of the store
    const unsubscribe = useCampaignStore.subscribe(
      (state) => {
        setPendingCount(calculatePendingCount(state));
      }
    );

    // Get initial count immediately
    const initialState = useCampaignStore.getState();
    setPendingCount(calculatePendingCount(initialState));

    return unsubscribe;
  }, []); // Empty dependency array since we're using subscriptions

  const handleManualSync = async () => {
    if (isSyncing) return;

    setIsSyncing(true);
    try {
      const result = await syncService.syncPendingData();
      
      if (onSyncComplete) {
        onSyncComplete(result);
      }

      if (!result.success && result.errors.length > 0) {
        Alert.alert(
          'Sync Issues',
          `Some items failed to sync:\n${result.errors.slice(0, 3).join('\n')}${result.errors.length > 3 ? '\n...' : ''}`,
          [{ text: 'OK' }]
        );
      }
    } catch {
      Alert.alert('Sync Error', 'Failed to sync data. Please try again.');
    } finally {
      setIsSyncing(false);
    }
  };

  // Check if there are pending sync items directly from store
  const hasPending = pendingCount.voters > 0 || pendingCount.addresses > 0 || pendingCount.telling > 0;

  if (!hasPending && !isSyncing) {
    return null; // Don't show anything if no pending sync
  }

  return (
    <View style={{
      position: 'absolute',
      bottom: 80 + insets.bottom,
      right: 16,
      backgroundColor: '#007AFF',
      borderRadius: 20,
      paddingHorizontal: 12,
      paddingVertical: 8,
      flexDirection: 'row',
      alignItems: 'center',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.25,
      shadowRadius: 3.84,
      elevation: 5,
      zIndex: 1000,
    }}>
      {isSyncing ? (
        <>
          <ActivityIndicator size="small" color="#fff" style={{ marginRight: 8 }} />
          <ThemedText style={{ color: '#fff', fontSize: 12, fontWeight: '600' }}>
            Syncing...
          </ThemedText>
        </>
      ) : (
        <>
          <MaterialIcons name="cloud-upload" size={16} color="#fff" style={{ marginRight: 6 }} />
          <TouchableOpacity onPress={handleManualSync} style={{ flexDirection: 'row', alignItems: 'center' }}>
            <ThemedText style={{ color: '#fff', fontSize: 12, fontWeight: '600' }}>
              Offline
            </ThemedText>
          </TouchableOpacity>
        </>
      )}
    </View>
  );
};
